<!-- Hero / Breadcrumb -->
<section class="refund-hero">
  <div class="container hero-inner">
    <div class="row align-items-center g-5">
      <div class="col-lg-7" data-aos="fade-up" data-aos-duration="1000" data-aos-once="true">
        <span class="breadcrumb-pill">
          <i class="ti ti-receipt-refund"></i>
          Refund Policy
        </span>
        <h1 class="mt-3 mb-3">
          Fair, transparent &amp;
          <span class="grad-text">hassle-free refunds</span>
        </h1>
        <p class="text-gray-9 mb-4" style="max-width: 560px">
          We stand behind AVR HRMS. If something isn’t right, our policy makes it simple to request
          a refund — no hidden steps, no fine print games.
        </p>
        <div class="d-flex flex-wrap gap-2">
          <a href="#process" class="btn btn-primary-gradient rounded btn-sm align-items-center">
            <i class="ti ti-route ms-1 me-2"></i>How it works
            <i class="ti ti-arrow-right ms-2"></i>
          </a>
          <a href="#faq" class="btn btn-secondary rounded btn-sm align-items-center">
            <i class="ti ti-help-circle me-2"></i>Read FAQs
          </a>
        </div>
        <div class="mt-4 d-flex flex-wrap gap-3 text-gray-9" style="font-size: 14px">
          <span><i class="ti ti-shield-check text-primary me-1"></i> Secure billing</span>
          <span><i class="ti ti-clock-hour-4 text-primary me-1"></i> 7-day window</span>
          <span><i class="ti ti-headset text-primary me-1"></i> 24/7 support</span>
        </div>
      </div>
      <div
        class="col-lg-5 hero-art"
        data-aos="zoom-in"
        data-aos-duration="1000"
        data-aos-once="true"
      >
        <div class="art-card text-center">
          <div class="art-icon"><i class="ti ti-cash-banknote"></i></div>
          <h4 class="mb-2">100% Money-Back Promise</h4>
          <p class="text-gray-9 mb-3">
            Eligible refunds are processed within
            <strong class="text-primary">5–7 business days</strong>
            to your original payment method.
          </p>
          <div class="badge-row">
            <span class="mini-badge"><i class="ti ti-credit-card"></i> Cards</span>
            <span class="mini-badge"><i class="ti ti-building-bank"></i> Bank</span>
            <span class="mini-badge"><i class="ti ti-brand-paypal"></i> PayPal</span>
            <span class="mini-badge"><i class="ti ti-wallet"></i> UPI</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
<!-- /Hero -->

<!-- Highlight cards -->
<section class="section-pad">
  <div class="container">
    <div class="text-center mb-5" data-aos="fade-up" data-aos-once="true">
      <span class="section-eyebrow"><i class="ti ti-sparkles"></i> What you should know</span>
      <h2 class="mt-3 fw-bold">Our refund policy at a glance</h2>
      <p class="text-gray-9 mx-auto" style="max-width: 640px">
        Four simple commitments that keep your purchase risk-free.
      </p>
    </div>
    <div class="row g-4">
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="100" data-aos-once="true">
        <div class="highlight-card text-center">
          <span class="highlight-icon"><i class="ti ti-clock-bolt"></i></span>
          <h5 class="fw-bold">7-Day Window</h5>
          <p class="text-gray-9 mb-0">Request a refund within 7 days of your purchase date.</p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="200" data-aos-once="true">
        <div class="highlight-card text-center">
          <span class="highlight-icon alt-1"><i class="ti ti-receipt-2"></i></span>
          <h5 class="fw-bold">Original Payment</h5>
          <p class="text-gray-9 mb-0">Funds are returned to the same method used at checkout.</p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="300" data-aos-once="true">
        <div class="highlight-card text-center">
          <span class="highlight-icon alt-2"><i class="ti ti-shield-check"></i></span>
          <h5 class="fw-bold">No Hidden Fees</h5>
          <p class="text-gray-9 mb-0">You receive the full eligible amount — no deductions.</p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="400" data-aos-once="true">
        <div class="highlight-card text-center">
          <span class="highlight-icon alt-3"><i class="ti ti-message-2-heart"></i></span>
          <h5 class="fw-bold">Friendly Support</h5>
          <p class="text-gray-9 mb-0">Our team replies to refund queries within 24 hours.</p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Process timeline -->
<section id="process" class="refund-process section-pad">
  <div class="container">
    <div class="text-center mb-5" data-aos="fade-up" data-aos-once="true">
      <span class="section-eyebrow"><i class="ti ti-route"></i> The Process</span>
      <h2 class="mt-3 fw-bold">Get your refund in 4 easy steps</h2>
    </div>
    <div class="row g-4">
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="100" data-aos-once="true">
        <div class="step-card">
          <span class="step-num">1</span>
          <i class="ti ti-mail-forward step-ico"></i>
          <h5 class="fw-bold mt-2">Reach out</h5>
          <p class="text-gray-9 mb-0">
            Email us at
            <a href="mailto:support@avrhrms.com" class="text-primary fw-semibold"
              >support&#64;avrhrms.com</a
            >
            with your order ID.
          </p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="200" data-aos-once="true">
        <div class="step-card">
          <span class="step-num">2</span>
          <i class="ti ti-file-search step-ico"></i>
          <h5 class="fw-bold mt-2">Quick review</h5>
          <p class="text-gray-9 mb-0">We verify your eligibility within 1–2 business days.</p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="300" data-aos-once="true">
        <div class="step-card">
          <span class="step-num">3</span>
          <i class="ti ti-circle-check step-ico"></i>
          <h5 class="fw-bold mt-2">Approval</h5>
          <p class="text-gray-9 mb-0">You’ll get a confirmation email once approved.</p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="400" data-aos-once="true">
        <div class="step-card">
          <span class="step-num">4</span>
          <i class="ti ti-cash step-ico"></i>
          <h5 class="fw-bold mt-2">Refund issued</h5>
          <p class="text-gray-9 mb-0">Funds reflect in your account within 5–7 business days.</p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Eligibility -->
<section class="section-pad">
  <div class="container">
    <div class="row g-5 align-items-start">
      <div class="col-lg-6" data-aos="fade-right" data-aos-once="true">
        <span class="section-eyebrow"><i class="ti ti-checks"></i> Eligible</span>
        <h3 class="mt-3 fw-bold">You can request a refund if…</h3>
        <div class="mt-3">
          <div class="eligible-row">
            <span class="icon-tick"><i class="ti ti-check"></i></span>
            <div>
              <strong>Purchase made within 7 days</strong>
              <p class="text-gray-9 mb-0">The request is raised within the eligible window.</p>
            </div>
          </div>
          <div class="eligible-row">
            <span class="icon-tick"><i class="ti ti-check"></i></span>
            <div>
              <strong>Major service issue</strong>
              <p class="text-gray-9 mb-0">A documented bug prevents core HRMS use.</p>
            </div>
          </div>
          <div class="eligible-row">
            <span class="icon-tick"><i class="ti ti-check"></i></span>
            <div>
              <strong>Duplicate charge</strong>
              <p class="text-gray-9 mb-0">Your card was charged twice for the same plan.</p>
            </div>
          </div>
          <div class="eligible-row">
            <span class="icon-tick"><i class="ti ti-check"></i></span>
            <div>
              <strong>Plan not activated</strong>
              <p class="text-gray-9 mb-0">You paid but never received access to the dashboard.</p>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6" data-aos="fade-left" data-aos-once="true">
        <span class="section-eyebrow" style="background: #fdecec; color: #ef4444">
          <i class="ti ti-alert-triangle"></i>
          Not Eligible
        </span>
        <h3 class="mt-3 fw-bold">Refunds are not granted when…</h3>
        <div class="mt-3">
          <div class="eligible-row">
            <span class="icon-cross"><i class="ti ti-x"></i></span>
            <div>
              <strong>Window has expired</strong>
              <p class="text-gray-9 mb-0">Requests after 7 days from purchase.</p>
            </div>
          </div>
          <div class="eligible-row">
            <span class="icon-cross"><i class="ti ti-x"></i></span>
            <div>
              <strong>Custom development work</strong>
              <p class="text-gray-9 mb-0">Bespoke modules &amp; integrations are non-refundable.</p>
            </div>
          </div>
          <div class="eligible-row">
            <span class="icon-cross"><i class="ti ti-x"></i></span>
            <div>
              <strong>Change of mind</strong>
              <p class="text-gray-9 mb-0">After the plan has been actively used.</p>
            </div>
          </div>
          <div class="eligible-row">
            <span class="icon-cross"><i class="ti ti-x"></i></span>
            <div>
              <strong>Policy violations</strong>
              <p class="text-gray-9 mb-0">Accounts suspended for terms-of-service breaches.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- FAQ -->
<section id="faq" class="section-pad" style="background: #fafbff">
  <div class="container">
    <div class="text-center mb-5" data-aos="fade-up" data-aos-once="true">
      <span class="section-eyebrow"><i class="ti ti-help"></i> FAQs</span>
      <h2 class="mt-3 fw-bold">Refund policy — frequently asked</h2>
    </div>
    <div class="row justify-content-center">
      <div class="col-lg-9" data-aos="fade-up" data-aos-once="true">
        <div class="accordion refund-faq" id="refundFaq">
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#faq1"
              >
                <i class="ti ti-clock me-2"></i>
                How long does the refund take?
              </button>
            </h2>
            <div id="faq1" class="accordion-collapse collapse show" data-bs-parent="#refundFaq">
              <div class="accordion-body">
                Approved refunds typically reflect in your account within
                <strong>5–7 business days</strong>, depending on your bank or payment provider.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#faq2"
              >
                <i class="ti ti-credit-card me-2"></i>
                Will I be refunded to a different account?
              </button>
            </h2>
            <div id="faq2" class="accordion-collapse collapse" data-bs-parent="#refundFaq">
              <div class="accordion-body">
                For security reasons, all refunds are issued back to the
                <strong>original payment method</strong>
                used at checkout.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#faq3"
              >
                <i class="ti ti-discount-2 me-2"></i>
                Do annual plans qualify for partial refunds?
              </button>
            </h2>
            <div id="faq3" class="accordion-collapse collapse" data-bs-parent="#refundFaq">
              <div class="accordion-body">
                Annual plans are refundable only within the 7-day window. After that, you can cancel
                auto-renewal but the current term is non-refundable.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#faq4"
              >
                <i class="ti ti-tools me-2"></i>
                Are setup &amp; customization fees refundable?
              </button>
            </h2>
            <div id="faq4" class="accordion-collapse collapse" data-bs-parent="#refundFaq">
              <div class="accordion-body">
                One-time setup, onboarding, and custom development charges are
                <strong>non-refundable</strong>
                once work has commenced.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#faq5"
              >
                <i class="ti ti-mail me-2"></i>
                How do I contact the refund team?
              </button>
            </h2>
            <div id="faq5" class="accordion-collapse collapse" data-bs-parent="#refundFaq">
              <div class="accordion-body">
                Email
                <a href="mailto:support@avrhrms.com" class="text-primary fw-semibold"
                  >support&#64;avrhrms.com</a
                >
                with your order ID and reason. We respond within 24 hours.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- CTA -->
<section class="section-pad">
  <div class="container">
    <div class="refund-cta text-center" data-aos="zoom-in" data-aos-once="true">
      <i class="ti ti-mood-happy" style="font-size: 54px; color: #ffb38a"></i>
      <h2 class="mt-3 mb-2" style="color: #ffffff">Need help with a refund?</h2>
      <p class="mb-4" style="opacity: 0.85; max-width: 560px; margin-inline: auto">
        Our team is here to make things right. Reach out and we’ll guide you through the process.
      </p>
      <div class="d-flex flex-wrap gap-2 justify-content-center">
        <a
          href="mailto:support@avrhrms.com"
          class="btn btn-primary-gradient rounded btn-sm align-items-center"
        >
          <i class="ti ti-mail me-2"></i>
          Email Support
          <i class="ti ti-arrow-right ms-2"></i>
        </a>
        <a
          href="https://wa.me/918107011186"
          target="_blank"
          class="btn btn-secondary rounded btn-sm align-items-center"
        >
          <i class="fa-brands fa-whatsapp me-2"></i>
          Chat on WhatsApp
        </a>
      </div>
    </div>
  </div>
</section>
